import { IServerSideDatasource } from 'ag-grid-enterprise';
import type {
  ColumnVisibleEvent,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridReadyEvent,
  GridSizeChangedEvent,
  ModelUpdatedEvent,
  PaginationChangedEvent,
  SortChangedEvent,
} from 'ag-grid-enterprise';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import globalStyles from '../../../styles/index.module.css';
import irStyles from '../../Instrument_Runs/styles.module.css';
import { loadColumnState, saveColumnState, cleanupInvalidColumnState } from '../utils/columnStateHelpers';

import { getInitialState, sampleTableGridReducer } from './useSampleTableGridReducer';

export interface PanelRef {
  current: {
    isCollapsed?: () => boolean;
    resize?: (percent: number) => void;
    expand?: () => void;
    collapse?: () => void;
  } | null;
}

interface UseSampleTableGridProps<T> {
  panelRef: PanelRef;
  adjacentPanelRef: PanelRef;
  studyId: string;
  validSortFields: string[];
  localStorageKeyFn: (studyId: string) => string;
  paginationPageSizeKeyFn: (studyId: string) => string;
  defaultPageSize?: number;
  datasource: IServerSideDatasource<T>;
}

export function useSampleTableGrid<T>({
  panelRef,
  adjacentPanelRef,
  studyId,
  validSortFields,
  localStorageKeyFn,
  paginationPageSizeKeyFn,
  defaultPageSize = 25,
  datasource,
}: UseSampleTableGridProps<T>) {
  const [state, dispatch] = useReducer(sampleTableGridReducer, undefined, () =>
    getInitialState(panelRef, studyId, paginationPageSizeKeyFn, defaultPageSize),
  );
  const firstDataRenderedRef = useRef(false);

  const handleTogglePanel = useCallback(() => {
    if (!panelRef.current) return;

    const adjacent = adjacentPanelRef?.current;

    if (state.isCollapsed) {
      if (
        adjacent &&
        typeof adjacent.isCollapsed === 'function' &&
        !adjacent.isCollapsed() &&
        typeof panelRef.current.resize === 'function' &&
        typeof adjacent.resize === 'function'
      ) {
        panelRef.current.resize(50);
        adjacent.resize(50);
        panelRef.current.expand?.();
        dispatch({ type: 'SET_COLLAPSED', value: false });
        return;
      }
      panelRef.current.expand?.();
      dispatch({ type: 'SET_COLLAPSED', value: false });
    } else {
      panelRef.current.collapse?.();
      dispatch({ type: 'SET_COLLAPSED', value: true });
    }
  }, [panelRef, adjacentPanelRef, state.isCollapsed]);

  useEffect(() => {
    cleanupInvalidColumnState(localStorageKeyFn(studyId), validSortFields, `study ${studyId}`);
  }, [studyId, localStorageKeyFn, validSortFields]);

  const tableContainerClasses = useMemo(
    () => ({ className: clsx(irStyles.proteoformsTable, globalStyles.scrollbars) }),
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    [],
  );

  // AG Grid event handlers
  const handleGridReady = useCallback(
    (e: GridReadyEvent<T>) => {
      const savedColumnState = loadColumnState(localStorageKeyFn(studyId), validSortFields);
      if (savedColumnState) {
        e.api.applyColumnState({ state: savedColumnState, applyOrder: true });
      }
      const currentSort = e.api
        .getColumnState()
        .find((col: { sort?: string | null; colId?: string }) => col.sort === 'asc' || col.sort === 'desc');
      if (!currentSort || (currentSort.colId && !validSortFields.includes(currentSort.colId))) {
        e.api.applyColumnState({
          state: [{ colId: validSortFields[0], sort: 'asc' }],
          defaultState: { sort: null },
        });
      }
      e.api.setGridOption('serverSideDatasource', datasource);
      e.api.updateGridOptions({ paginationPageSize: state.pageSize });
    },
    [localStorageKeyFn, studyId, validSortFields, datasource, state.pageSize],
  );

  const handleFirstDataRendered = useCallback((e: FirstDataRenderedEvent<T>) => {
    firstDataRenderedRef.current = true;
    dispatch({ type: 'SET_FIRST_DATA_RENDERED', value: true });
    const rowCount = e.api.paginationGetRowCount();
    if (rowCount >= 0) {
      dispatch({ type: 'SET_TOTAL_COUNT', value: rowCount });
    }
    e.api.sizeColumnsToFit();
  }, []);

  // DRY: Single handler for column state changes (sort, visibility, etc.)
  const handleColumnStateChange = useCallback(
    (e: SortChangedEvent<T> | ColumnVisibleEvent<T>) => {
      saveColumnState(localStorageKeyFn(studyId), e.api.getColumnState(), validSortFields);
    },
    [localStorageKeyFn, studyId, validSortFields],
  );

  const handleFilterChanged = useCallback((e: FilterChangedEvent<T>) => {
    if (!firstDataRenderedRef.current) return;
    const rowCount = e.api.paginationGetRowCount();
    if (rowCount >= 0) {
      dispatch({ type: 'SET_TOTAL_COUNT', value: rowCount });
    }
  }, []);

  const handleModelUpdated = useCallback((e: ModelUpdatedEvent<T>) => {
    if (!firstDataRenderedRef.current) return;
    const rowCount = e.api.paginationGetRowCount();
    if (rowCount >= 0) {
      dispatch({ type: 'SET_TOTAL_COUNT', value: rowCount });
    }
  }, []);

  const handlePaginationChanged = useCallback(
    (e: PaginationChangedEvent<T>) => {
      const newPageSize = e.api.paginationGetPageSize();
      dispatch({ type: 'SET_PAGE_SIZE', value: newPageSize });
      localStorage.setItem(paginationPageSizeKeyFn(studyId), newPageSize.toString());
    },
    [paginationPageSizeKeyFn, studyId],
  );

  const handleGridSizeChanged = useCallback((e: GridSizeChangedEvent<T>) => {
    e.api.sizeColumnsToFit();
  }, []);

  return {
    isCollapsed: state.isCollapsed,
    setIsCollapsed: (value: boolean) => dispatch({ type: 'SET_COLLAPSED', value }),
    pageSize: state.pageSize,
    totalCount: state.totalCount,
    firstDataRenderedRef,
    handleTogglePanel,
    tableContainerClasses,
    defaultColDef,
    handleGridReady,
    handleFirstDataRendered,
    handleSortChanged: handleColumnStateChange,
    handleColumnVisible: handleColumnStateChange,
    handleFilterChanged,
    handleModelUpdated,
    handlePaginationChanged,
    handleGridSizeChanged,
    dispatch,
  };
}
